// Stable presentation keys shared by producers and consumers. Never send arbitrary prose in params.
import type { NotificationKind } from './enums.js';

/** Required interpolation parameters for audit.detail.<key>. */
export const AUDIT_DETAIL_PARAMS = {
  role_names: ["roles", "teams"],
  policy_change: ["path", "from", "to"],
  "reason_too_short": [
    "min"
  ],
  "verify_ok": [
    "n"
  ],
  "verify_bad": [
    "n",
    "seq"
  ],
  "verify_anchor": [
    "n"
  ],
  "anchor": [
    "date"
  ],
  "review": [
    "month",
    "verdict"
  ],
  "export_rows": [
    "rows",
    "format"
  ],
  "export_identity": [],
  "grant": [
    "role"
  ],
  "revoke": [
    "role"
  ],
  "perm": [
    "role",
    "perm",
    "sFrom",
    "sTo"
  ],
  "teams": [
    "teams"
  ],
  "until": [
    "date"
  ],
  "undo": [],
  "code": [
    "code"
  ],
  "level": [
    "levelFrom",
    "levelTo"
  ],
  "pause": [
    "until"
  ],
  "resume": [],
  "withdraw": [],
  "withdraw_undo": [],
  "agree": [
    "notice"
  ],
  "login_method": [
    "method"
  ],
  "attempt": [
    "n"
  ],
  "sessions_ended": [
    "n"
  ],
  "notice": [
    "from",
    "to"
  ],
  "retention_window": [
    "setting",
    "from",
    "to"
  ]
} as const;
export type AuditDetailKey = keyof typeof AUDIT_DETAIL_PARAMS;

/** Optional parameters selecting richer notification wording; an empty object uses the general sentence. */
export const NOTIFICATION_PARAM_KEYS = {
  "device_down": [
    "tool",
    "days"
  ],
  "collection_unknown": ["count"],
  "collection_gap": [
    "minutes"
  ],
  "reconsent": [
    "version"
  ],
  "record_viewed": [
    "reasonCategory"
  ],
  "token_expiring": [
    "days"
  ],
  "budget_warning": [],
  "version_checking": [
    "tool",
    "version"
  ],
  "bulk_view": [
    "count",
    "minutes"
  ],
  "link_broken": [
    "count"
  ],
  "usage_spike": [],
  "team_update": [],
  "policy_changed": [],
  "deletion_processed": [
    "count"
  ],
  "approval_pending": [
    "count"
  ],
  "grant_expiring": [
    "days"
  ],
  "grant_expiry_deferred": [],
  "grant_dormant": ["days"],
  "audit_summary": [
    "count"
  ]
} as const satisfies Record<NotificationKind, readonly string[]>;

/** event.other is used for names not yet in this catalog; rawName remains available separately. */
export const EVENT_LABEL_KEYS = [
  "event.user_prompt",
  "event.answer",
  "event.assistant_response",
  "event.api_request",
  "event.api_error",
  "event.api_retries_exhausted",
  "event.api_refusal",
  "event.tool_decision",
  "event.tool_result",
  "event.content_view",
  "event.permission_mode_changed",
  "event.subagent_completed",
  "event.skill_activated",
  "event.compaction",
  "event.auth",
  "event.internal_error",
  "event.mcp_server_connection",
  "event.plugin_installed",
  "event.plugin_loaded",
  "event.hook_execution_start",
  "event.hook_execution_complete",
  "event.managed_settings_resolved",
  "event.hook_registered",
  "event.conversation_starts",
  "event.agent_response",
  "event.websocket_request",
  "event.websocket_connect",
  "event.sse_event",
  "event.websocket_event",
  "event.sandbox_outcome",
  "event.turn_cost",
  "event.turn_ttft",
  "event.startup_phase",
  "event.auth_recovery",
  "event.plugin_install",
  "event.guardian",
  "event.other"
] as const;
